"use client";

import { useState, useEffect } from "react";
import { FormLayout } from "../classfields_components/FormLayout";
import { ClassifiedPreview } from "./ClassifieldPreview";

export function EditClassifiedForm({
  classifiedId,
  initialData,
  onSave,
  onCancel,
  onClose,
}) {
  // Form state
  const [formData, setFormData] = useState(initialData || {});
  const [originalData, setOriginalData] = useState(null); // Store original data for comparison
  const [loading, setLoading] = useState(!initialData);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});

  // Helper function to convert details array to object
  const convertDetailsToObject = (detailsArray) => {
    if (!Array.isArray(detailsArray)) return {};

    const detailsObj = {};
    detailsArray.forEach((detail) => {
      if (detail.key && detail.value !== undefined) {
        // Handle special cases for boolean and array values
        if (detail.key === "isUtilities" || detail.key === "isPreowned") {
          detailsObj[detail.key] =
            detail.value === "true" || detail.value === true;
        } else if (detail.key === "amenities") {
          detailsObj[detail.key] =
            typeof detail.value === "string"
              ? detail.value.split(",").filter(Boolean)
              : detail.value;
        } else if (
          detail.key === "condition" ||
          detail.key === "bedrooms" ||
          detail.key === "bathrooms" ||
          detail.key === "squareFoot" ||
          detail.key === "totalLeaseLength" ||
          detail.key === "remainingLease" ||
          detail.key === "kilometers"
        ) {
          detailsObj[detail.key] = Number(detail.value);
        } else {
          detailsObj[detail.key] = detail.value;
        }
      }
    });
    return detailsObj;
  };

  // Helper function to convert details object back to array format
  const convertDetailsToArray = (detailsObj) => {
    const detailsArray = [];
    Object.entries(detailsObj).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        let processedValue = value;

        // Handle special cases
        if (key === "amenities" && Array.isArray(value)) {
          processedValue = value.join(",");
        } else if (typeof value === "boolean") {
          processedValue = value.toString();
        } else if (typeof value === "number") {
          processedValue = value.toString();
        }

        detailsArray.push({
          key,
          value: processedValue,
          listing_id: formData.id || classifiedId,
        });
      }
    });
    return detailsArray;
  };

  // Helper function to deep compare values
  const deepEqual = (a, b) => {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((val, index) => val === sortedB[index]);
    }
    return false;
  };

  // Helper function to get only changed fields
  const getChangedFields = (original, current) => {
    const changes = {};

    // Compare specific details
    const originalDetails = original?.details
      ? convertDetailsToObject(original.details)
      : {};
    const currentDetails = current?.details
      ? convertDetailsToObject(current.details)
      : {};

    // Check each field in current details
    Object.keys(currentDetails).forEach((key) => {
      const currentValue = currentDetails[key];
      const originalValue = originalDetails[key];

      if (!deepEqual(currentValue, originalValue)) {
        changes[key] = {
          from: originalValue,
          to: currentValue,
        };
      }
    });

    // Check for removed fields
    Object.keys(originalDetails).forEach((key) => {
      if (!(key in currentDetails)) {
        changes[key] = {
          from: originalDetails[key],
          to: null,
        };
      }
    });

    return changes;
  };

  // Extract category and specific details
  const category = formData.category_name || "General";
  const specificDetails = formData.details
    ? convertDetailsToObject(formData.details)
    : {};

  // Update formData when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      setOriginalData(JSON.parse(JSON.stringify(initialData))); // Deep copy for comparison
      setLoading(false);
    }
  }, [initialData]);

  // Load data from API if not provided
  useEffect(() => {
    if (!initialData && classifiedId) {
      loadClassifiedData();
    }
  }, [classifiedId, initialData]);

  const loadClassifiedData = async () => {
    try {
      setLoading(true);
      // Replace with your actual API call
      const response = await fetch(`/api/classifieds/${classifiedId}`);
      const data = await response.json();
      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(data))); // Deep copy for comparison
    } catch (error) {
      console.error("Failed to load classified data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Update form data (only for editable fields)
  const updateFormData = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // Handle amenity changes
  const handleAmenityChange = (amenity, checked) => {
    const currentAmenities = specificDetails.amenities || [];
    let newAmenities;

    if (checked) {
      newAmenities = [...currentAmenities, amenity];
    } else {
      newAmenities = currentAmenities.filter((a) => a !== amenity);
    }

    const updatedDetails = {
      ...specificDetails,
      amenities: newAmenities,
    };

    updateFormData({
      details: convertDetailsToArray(updatedDetails),
    });
  };

  // Handle specific detail changes
  const handleSpecificDetailChange = (field, value) => {
    const updatedDetails = {
      ...specificDetails,
      [field]: value,
    };

    updateFormData({
      details: convertDetailsToArray(updatedDetails),
    });

    // Clear errors for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Validation (only for editable fields)
  const validateForm = () => {
    const newErrors = {};

    // Category-specific validation for editable fields only
    switch (category.toLowerCase()) {
      case "house":
      case "sublease":
        if (!specificDetails.propertyType) {
          newErrors.propertyType = "Property type is required";
        }
        if (!specificDetails.bedrooms) {
          newErrors.bedrooms = "Bedrooms is required";
        }
        if (!specificDetails.bathrooms) {
          newErrors.bathrooms = "Bathrooms is required";
        }
        if (
          specificDetails.condition &&
          (specificDetails.condition < 1 || specificDetails.condition > 10)
        ) {
          newErrors.condition = "Condition must be between 1-10";
        }
        break;

      case "auto":
        if (
          specificDetails.condition &&
          (specificDetails.condition < 1 || specificDetails.condition > 10)
        ) {
          newErrors.condition = "Condition must be between 1-10";
        }
        if (!specificDetails.engineType) {
          newErrors.engineType = "Engine type is required";
        }
        break;

      case "electronics":
      case "appliance":
        if (
          specificDetails.condition &&
          (specificDetails.condition < 1 || specificDetails.condition > 10)
        ) {
          newErrors.condition = "Condition must be between 1-10";
        }
        break;

      case "apparel":
        if (!specificDetails.apparelType) {
          newErrors.apparelType = "Apparel type is required";
        }
        if (!specificDetails.size) {
          newErrors.size = "Size is required";
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save changes
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);

      // Get only the changed fields
      const changedFields = getChangedFields(originalData, formData);

      await new Promise((resolve) => setTimeout(resolve, 1000));
      const submitData = {
        ...formData,
        details: convertDetailsToArray(specificDetails),
        specific_details: specificDetails,
      };

      // Call the onSave callback with the updated data
      if (onSave) {
        onSave(submitData);
      }
    } catch (error) {
      console.error("Failed to save classified:", error);
    } finally {
      setSaving(false);
    }
  };

  // Render category-specific fields
  const renderCategorySpecificFields = () => {
    const inputClassName =
      "w-full p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-[#7B189F] transition-colors";
    const errorInputClassName =
      "w-full p-4 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors";

    switch (category.toLowerCase()) {
      case "house":
      case "sublease":
        return (
          <div className="space-y-4">
            {/* Property Type */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Property Type
              </label>
              <select
                value={specificDetails.propertyType || ""}
                onChange={(e) =>
                  handleSpecificDetailChange("propertyType", e.target.value)
                }
                className={
                  errors.propertyType ? errorInputClassName : inputClassName
                }
              >
                <option value="">Select property type</option>
                <option value="apartment">Apartment</option>
                <option value="house">House</option>
                <option value="condo">Condo</option>
                <option value="townhouse">Townhouse</option>
                {category.toLowerCase() === "sublease" && (
                  <>
                    <option value="studio">Studio</option>
                    <option value="loft">Loft</option>
                    <option value="duplex">Duplex</option>
                  </>
                )}
              </select>
              {errors.propertyType && (
                <p className="text-red-500 text-sm">{errors.propertyType}</p>
              )}
            </div>

            {/* Bedrooms and Bathrooms */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Bedrooms
                </label>
                <select
                  value={specificDetails.bedrooms || ""}
                  onChange={(e) =>
                    handleSpecificDetailChange(
                      "bedrooms",
                      Number(e.target.value)
                    )
                  }
                  className={
                    errors.bedrooms ? errorInputClassName : inputClassName
                  }
                >
                  <option value="">Select bedrooms</option>
                  {category.toLowerCase() === "sublease" && (
                    <option value="studio">Studio</option>
                  )}
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5+</option>
                </select>
                {errors.bedrooms && (
                  <p className="text-red-500 text-sm">{errors.bedrooms}</p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Bathrooms
                </label>
                <select
                  value={specificDetails.bathrooms || ""}
                  onChange={(e) =>
                    handleSpecificDetailChange(
                      "bathrooms",
                      Number(e.target.value)
                    )
                  }
                  className={
                    errors.bathrooms ? errorInputClassName : inputClassName
                  }
                >
                  <option value="">Select bathrooms</option>
                  <option value="1">1</option>
                  <option value="1.5">1.5</option>
                  <option value="2">2</option>
                  <option value="2.5">2.5</option>
                  <option value="3">3+</option>
                </select>
                {errors.bathrooms && (
                  <p className="text-red-500 text-sm">{errors.bathrooms}</p>
                )}
              </div>
            </div>

            {/* Square Footage */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Square Footage
              </label>
              <input
                type="number"
                min="1"
                value={specificDetails.squareFoot || ""}
                onChange={(e) =>
                  handleSpecificDetailChange(
                    "squareFoot",
                    Number(e.target.value)
                  )
                }
                placeholder="Enter square footage"
                className={inputClassName}
              />
            </div>

            {/* Sublease specific fields */}
            {category.toLowerCase() === "sublease" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Total Lease (months)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={specificDetails.totalLeaseLength || ""}
                      onChange={(e) =>
                        handleSpecificDetailChange(
                          "totalLeaseLength",
                          Number(e.target.value)
                        )
                      }
                      className={inputClassName}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Remaining Lease (months)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={specificDetails.remainingLease || ""}
                      onChange={(e) =>
                        handleSpecificDetailChange(
                          "remainingLease",
                          Number(e.target.value)
                        )
                      }
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Agreement Type
                  </label>
                  <select
                    value={specificDetails.agreementType || ""}
                    onChange={(e) =>
                      handleSpecificDetailChange(
                        "agreementType",
                        e.target.value
                      )
                    }
                    className={inputClassName}
                  >
                    <option value="">Select agreement type</option>
                    <option value="student">Student</option>
                    <option value="professional">Professional</option>
                    <option value="family">Family</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Utilities
                  </label>
                  <div className="flex gap-6">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="utilities"
                        checked={specificDetails.isUtilities === true}
                        onChange={() =>
                          handleSpecificDetailChange("isUtilities", true)
                        }
                        className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                      />
                      <span className="ml-2">Included</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="utilities"
                        checked={specificDetails.isUtilities === false}
                        onChange={() =>
                          handleSpecificDetailChange("isUtilities", false)
                        }
                        className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                      />
                      <span className="ml-2">Not Included</span>
                    </label>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Furnished Status
                  </label>
                  <div className="flex gap-6">
                    {["furnished", "semi-furnished", "unfurnished"].map(
                      (status) => (
                        <label key={status} className="flex items-center">
                          <input
                            type="radio"
                            name="furnished"
                            checked={specificDetails.furnished === status}
                            onChange={() =>
                              handleSpecificDetailChange("furnished", status)
                            }
                            className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                          />
                          <span className="ml-2 capitalize">
                            {status.replace("-", " ")}
                          </span>
                        </label>
                      )
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Amenities for House and Sublease */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Amenities
              </label>
              <div className="flex flex-wrap gap-3">
                {[
                  { id: "parking", label: "Parking", icon: "🚗" },
                  { id: "wifi", label: "Wi-Fi", icon: "📶" },
                  {
                    id: "air-conditioning",
                    label: "Air Conditioning",
                    icon: "❄️",
                  },
                  { id: "heating", label: "Heating", icon: "🔥" },
                  { id: "pet-friendly", label: "Pet-Friendly", icon: "🐕" },
                  {
                    id: "laundry-in-unit",
                    label: "Laundry In-Unit",
                    icon: "👕",
                  },
                  {
                    id: "washer-dryer-on-site",
                    label: "Washer/Dryer On-Site",
                    icon: "🔄",
                  },
                  { id: "balcony-patio", label: "Balcony/Patio", icon: "🏠" },
                  { id: "fireplace", label: "Fireplace", icon: "🔥" },
                  {
                    id: "fully-equipped-kitchen",
                    label: "Fully Equipped Kitchen",
                    icon: "🍳",
                  },
                  { id: "gym", label: "Gym", icon: "💪" },
                  {
                    id: "gated-community",
                    label: "Gated Community",
                    icon: "🏘️",
                  },
                  {
                    id: "security-system",
                    label: "Security System",
                    icon: "🔒",
                  },
                ].map((amenity) => (
                  <button
                    key={amenity.id}
                    type="button"
                    onClick={() =>
                      handleAmenityChange(
                        amenity.id,
                        !(specificDetails.amenities || []).includes(amenity.id)
                      )
                    }
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors ${
                      (specificDetails.amenities || []).includes(amenity.id)
                        ? "bg-black text-white border-black"
                        : "bg-white text-gray-700 border-gray-300 hover:border-gray-400"
                    }`}
                  >
                    <span>{amenity.icon}</span>
                    <span className="text-sm font-medium">{amenity.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case "auto":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Engine Type
              </label>
              <div className="flex gap-6">
                {["petrol", "diesel", "ev"].map((type) => (
                  <label key={type} className="flex items-center">
                    <input
                      type="radio"
                      name="engineType"
                      checked={specificDetails.engineType === type}
                      onChange={() =>
                        handleSpecificDetailChange("engineType", type)
                      }
                      className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                    />
                    <span className="ml-2 capitalize">
                      {type === "ev" ? "Electric Vehicle" : type}
                    </span>
                  </label>
                ))}
              </div>
              {errors.engineType && (
                <p className="text-red-500 text-sm">{errors.engineType}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Kilometers
              </label>
              <input
                type="number"
                min="0"
                value={specificDetails.kilometers || ""}
                onChange={(e) =>
                  handleSpecificDetailChange(
                    "kilometers",
                    Number(e.target.value)
                  )
                }
                placeholder="Enter kilometers"
                className={inputClassName}
              />
            </div>
          </div>
        );

      case "electronics":
      case "appliance":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Model Release Year
              </label>
              <input
                type="text"
                value={specificDetails.modelReleaseYear || ""}
                onChange={(e) =>
                  handleSpecificDetailChange("modelReleaseYear", e.target.value)
                }
                placeholder="e.g., 2023"
                className={inputClassName}
              />
            </div>
          </div>
        );

      case "apparel":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Apparel Type
              </label>
              <select
                value={specificDetails.apparelType || ""}
                onChange={(e) =>
                  handleSpecificDetailChange("apparelType", e.target.value)
                }
                className={
                  errors.apparelType ? errorInputClassName : inputClassName
                }
              >
                <option value="">Select type</option>
                <option value="shoes">Shoes</option>
                <option value="tshirt">T-Shirt</option>
                <option value="pants">Pants/Jeans</option>
                <option value="dress">Dress</option>
                <option value="jacket">Jacket/Coat</option>
                <option value="accessories">Accessories</option>
                <option value="other">Other</option>
              </select>
              {errors.apparelType && (
                <p className="text-red-500 text-sm">{errors.apparelType}</p>
              )}
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Size
              </label>
              <select
                value={specificDetails.size || ""}
                onChange={(e) =>
                  handleSpecificDetailChange("size", e.target.value)
                }
                className={errors.size ? errorInputClassName : inputClassName}
              >
                <option value="">Select size</option>
                <option value="xs">XS</option>
                <option value="s">S</option>
                <option value="m">M</option>
                <option value="l">L</option>
                <option value="xl">XL</option>
                <option value="xxl">XXL</option>
                <option value="other">Other</option>
              </select>
              {errors.size && (
                <p className="text-red-500 text-sm">{errors.size}</p>
              )}
            </div>
          </div>
        );

      case "roommate":
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Gender Preference
              </label>
              <div className="flex gap-6">
                {["no-preference", "male", "female"].map((pref) => (
                  <label key={pref} className="flex items-center">
                    <input
                      type="radio"
                      name="genderPreference"
                      checked={specificDetails.genderPreference === pref}
                      onChange={() =>
                        handleSpecificDetailChange("genderPreference", pref)
                      }
                      className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                    />
                    <span className="ml-2 capitalize">
                      {pref.replace("-", " ")}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Age Range
              </label>
              <select
                value={specificDetails.ageRange || ""}
                onChange={(e) =>
                  handleSpecificDetailChange("ageRange", e.target.value)
                }
                className={inputClassName}
              >
                <option value="">Select age range</option>
                <option value="18-22">18-22</option>
                <option value="23-27">23-27</option>
                <option value="28-32">28-32</option>
                <option value="33-37">33-37</option>
                <option value="38+">38+</option>
                <option value="any">Any Age</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Move-in Date
                </label>
                <input
                  type="date"
                  value={specificDetails.moveInDate || ""}
                  onChange={(e) =>
                    handleSpecificDetailChange("moveInDate", e.target.value)
                  }
                  className={inputClassName}
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Lease Duration
                </label>
                <select
                  value={specificDetails.leaseDuration || ""}
                  onChange={(e) =>
                    handleSpecificDetailChange("leaseDuration", e.target.value)
                  }
                  className={inputClassName}
                >
                  <option value="">Select duration</option>
                  <option value="3-months">3 Months</option>
                  <option value="6-months">6 Months</option>
                  <option value="1-year">1 Year</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Render step content
  const renderStepContent = () => {
    const disabledInputClassName =
      "w-full p-4 border border-gray-200 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed";
    const inputClassName =
      "w-full p-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-[#7B189F] transition-colors";

    switch (currentStep) {
      case 1: // Basic Info (Read-only)
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-medium font-fraunces mb-4">
                Basic Information
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                The following information is read-only and cannot be edited.
              </p>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    type="text"
                    value={formData.title || ""}
                    disabled
                    className={disabledInputClassName}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <textarea
                    rows={4}
                    value={formData.description || ""}
                    disabled
                    className={disabledInputClassName}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Reason to Sell
                  </label>
                  <textarea
                    rows={3}
                    value={formData.reasonToSell || ""}
                    disabled
                    className={disabledInputClassName}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Price
                  </label>
                  <input
                    type="text"
                    value={
                      formData.price
                        ? `$${Number(formData.price).toLocaleString()}`
                        : ""
                    }
                    disabled
                    className={disabledInputClassName}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Address Line 1
                    </label>
                    <input
                      type="text"
                      value={formData.addressLine1 || ""}
                      disabled
                      className={disabledInputClassName}
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      value={formData.addressLine2 || ""}
                      disabled
                      className={disabledInputClassName}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.state || ""}
                    disabled
                    className={disabledInputClassName}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category_name || ""}
                    disabled
                    className={disabledInputClassName}
                  />
                </div>

                {/* Seller Information */}
                <div className="border-t pt-6 mt-6">
                  <h3 className="text-lg font-medium font-fraunces mb-4">
                    Seller Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Full Name
                      </label>
                      <input
                        type="text"
                        value={formData.fullName || ""}
                        disabled
                        className={disabledInputClassName}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Phone Number
                      </label>
                      <input
                        type="text"
                        value={formData.phoneNumber || ""}
                        disabled
                        className={disabledInputClassName}
                      />
                    </div>
                  </div>
                  <div className="space-y-2 mt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <input
                      type="text"
                      value={formData.email || ""}
                      disabled
                      className={disabledInputClassName}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 2: // Specific Details (Editable)
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-medium font-fraunces mb-4">
                Edit {category} Details
              </h2>
              <p className="text-sm text-gray-600 mb-6">
                You can edit the following details and amenities for your
                listing.
              </p>

              {/* Common fields */}
              <div className="space-y-4 mb-6">
                {/* Condition rating for applicable categories */}
                {[
                  "house",
                  "sublease",
                  "auto",
                  "electronics",
                  "appliance",
                  "apparel",
                ].includes(category.toLowerCase()) && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Condition Rating (1-10)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={specificDetails.condition || ""}
                      onChange={(e) =>
                        handleSpecificDetailChange(
                          "condition",
                          Number(e.target.value)
                        )
                      }
                      placeholder="Rate from 1-10"
                      className={
                        errors.condition
                          ? "w-full p-4 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors"
                          : inputClassName
                      }
                    />
                    {errors.condition && (
                      <p className="text-red-500 text-sm">{errors.condition}</p>
                    )}
                  </div>
                )}

                {/* Preowned status for applicable categories */}
                {[
                  "electronics",
                  "appliance",
                  "auto",
                  "house",
                  "sublease",
                ].includes(category.toLowerCase()) && (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Condition
                    </label>
                    <div className="flex gap-6">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`isPreowned-${category}`}
                          checked={specificDetails.isPreowned === true}
                          onChange={() =>
                            handleSpecificDetailChange("isPreowned", true)
                          }
                          className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                        />
                        <span className="ml-2">Preowned</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`isPreowned-${category}`}
                          checked={specificDetails.isPreowned === false}
                          onChange={() =>
                            handleSpecificDetailChange("isPreowned", false)
                          }
                          className="h-4 w-4 text-[#7B189F] border-gray-300 focus:ring-[#7B189F] accent-[#7B189F]"
                        />
                        <span className="ml-2">New</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Category-specific fields */}
              {renderCategorySpecificFields()}
            </div>
          </div>
        );

      case 3: // Preview
        return (
          <ClassifiedPreview
            formData={{
              ...formData,
              specific_details: specificDetails,
              // Ensure isPreowned is accessible at the top level for the preview
              isPreowned: specificDetails.isPreowned,
            }}
            category={category}
          />
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7B189F] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading classified data...</p>
        </div>
      </div>
    );
  }

  const getStepInfo = () => {
    switch (currentStep) {
      case 1:
        return {
          title: "View Listing Info",
          description: "Basic information (read-only)",
        };
      case 2:
        return {
          title: "Edit Details",
          description: "Update specific details and amenities",
        };
      case 3:
        return {
          title: "Preview Changes",
          description: "Review your updated listing",
        };
      default:
        return { title: "Edit Classified", description: "" };
    }
  };

  const { title, description } = getStepInfo();

  return (
    <FormLayout
      title={title}
      description={description}
      step={currentStep}
      maxSteps={3}
      onNext={
        currentStep === 3
          ? handleSave
          : () => setCurrentStep((prev) => Math.min(prev + 1, 3))
      }
      onBack={() => setCurrentStep((prev) => Math.max(prev - 1, 1))}
      onClose={onClose}
      isLastStep={currentStep === 3}
      isNextDisabled={saving}
    >
      {renderStepContent()}
    </FormLayout>
  );
}
